import {
  DataResponse,
  Merchant,
  MerchantDownloadRequest,
  MerchantFilters,
  MerchantListRequest,
} from "shared/models";
import { baseQuery } from "./baseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";
import { v4 as uuidv4 } from "uuid";

const MERCHANT_PAGE_NAME = "Merchant Management";

// Multi-select filters travel as one comma-joined value; an empty selection is dropped from the query.
const toCsvParam = (values?: string[]) => (values?.length ? values.join(",") : undefined);

// Map the screen's filter values onto query params, omitting anything the user left blank so the
// backend skips that condition (RTK Query strips `undefined` keys before serializing).
const buildFilterParams = (filters: MerchantFilters) => ({
  search: filters.search || undefined,
  type: filters.type || undefined,
  partnerId: filters.partnerId || undefined,
  country: filters.country || undefined,
  threeDsOnline: toCsvParam(filters.threeDsOnline),
  threeDsVirtual: toCsvParam(filters.threeDsVirtual),
  chargeCurrency: toCsvParam(filters.chargeCurrency),
  payoutCurrency: toCsvParam(filters.payoutCurrency),
  payoutChannel: toCsvParam(filters.payoutChannel),
  payoutTiming: toCsvParam(filters.payoutTiming),
  payoutFrequency: toCsvParam(filters.payoutFrequency),
});

export const merchantService = createApi({
  reducerPath: "merchantService",
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getMerchants: builder.query<DataResponse<Merchant[]>, MerchantListRequest>({
      query: ({ page, limit, sortBy, order, skip_log, ...filters }) => ({
        url: `/merchant-management`,
        params: {
          page,
          limit,
          include: "disbursement_config,banks",
          skip_log: skip_log ? skip_log : false,
          sortBy: sortBy || undefined,
          order: order || undefined,
          ...buildFilterParams(filters),
        },
        method: "GET",
        headers: !skip_log
          ? {
              PageName: MERCHANT_PAGE_NAME,
            }
          : {},
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success === true) {
          const data = rawData?.data.map((raw: any) => ({
            value: raw.id,
            ...raw,
            ...{ ...raw.disbursement_config },
          }));

          return { ...rawData, data, labelPaidOn: rawData.disbursement_config?.label };
        }
      },
    }),
    // Same listing endpoint as getMerchants; `download` switches it into export mode and `role_user`
    // picks the exported column set. Declared as a mutation (despite the GET verb) so each click
    // always hits the backend for a fresh export job instead of replaying a cached response.
    downloadMerchants: builder.mutation<DataResponse<{ id: number }>, MerchantDownloadRequest>({
      query: ({ download, role_user, sortBy, order, ...rest }) => {
        const {
          charge_mode,
          receive_notification,
          email_notification,
          user_name,
          report_type,
          url_created_date,
          url_attachment,
          ...filters
        } = rest;

        return {
          url: `/merchant-management`,
          params: {
            download,
            role_user,
            sortBy: sortBy || undefined,
            order: order || undefined,
            charge_mode,
            receive_notification,
            email_notification: email_notification || undefined,
            user_name,
            report_type,
            url_created_date,
            url_attachment,
            ...buildFilterParams(filters),
          },
          method: "GET",
          headers: {
            PageName: MERCHANT_PAGE_NAME,
          },
        };
      },
    }),
    getMerchantsSelect: builder.query<
      any,
      { is_cancelled?: boolean, page?: number; limit?: number; search?: string; skip_log?: boolean; partnerId?: number }
    >({
      query: (params) => ({
        // url: `/merchant-management?include=disbursement_config&type=select`,
        url: `/merchant-management/selection`,
        params,
        method: "GET",
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success === true) {
          const data = rawData?.data.map((raw: any) => ({
            value: raw.id,
            name: raw.trading_name,
            status: raw.status,
            // disbursementCurrency: raw?.disbursement_config?.currency,
          }));

          const sort = data.sort((a: any, b: any) => a?.name.localeCompare(b?.name));
          return { data: sort, pagination: rawData.meta };
        }
      },
    }),
    getMerchantDetail: builder.query<DataResponse<any>, { id: string; skip_log?: boolean }>({
      query: (params) => ({
        url: `/merchant-management/${params?.id}?skip_log=${
          params?.skip_log ? params?.skip_log : false
        }`,
        method: "GET",
        headers: !params?.skip_log
          ? {
              PageName: "Merchant Management",
            }
          : {},
      }),
    }),
    createMerchant: builder.mutation<DataResponse<any>, any>({
      query: (body: any) => ({
        url: "/merchant-management",
        body,
        method: "POST",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    updateMerchant: builder.mutation<DataResponse<any>, any>({
      query: (body: any) => {
        return {
          url: `/merchant-management/${body.id}`,
          body: body,
          params: { skip_log: body?.skip_log ? body?.skip_log : false },
          method: "PUT",
          headers: {
            PageName: "Merchant Management",
          },
        };
      },
    }),
    updateStatusMerchant: builder.mutation<any, { id: number; status: string, is_cancelled: boolean }>({
      query: (body: any) => ({
        url: `/merchant-management/${body.id}/updateStatus`,
        body,
        method: "PUT",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    updateBillingInfo: builder.mutation<
      any,
      {
        id: number;
        amount: number;
        currency: string;
        type: string;
        merchant_id: number;
        active: boolean;
      }
    >({
      query: (body: any) => ({
        url: `/merchant-management/${body.merchant_id}/billing/${body.id}`,
        body,
        method: "PUT",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    createBillingInfo: builder.mutation<
      any,
      {
        amount: number;
        currency: string;
        type: string;
        merchant_id: number;
        active: boolean;
      }
    >({
      query: (body: any) => ({
        url: `/merchant-management/${body.merchant_id}/billing`,
        body,
        method: "POST",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    getMerchantBilling: builder.query<DataResponse<any>, { id: number }>({
      query: (params) => ({
        url: `/merchant-management/${params?.id}/billing`,
        method: "GET",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    deleteMerchant: builder.mutation<DataResponse<any>, any>({
      query: (body: any) => ({
        url: `/merchant-management/${body.id}`,
        body,
        method: "DELETE",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    getPayoutAccountDetail: builder.query<any, { merchantId: number; id: number }>({
      query: (params) => ({
        url: `/merchant-management/${params.merchantId}/payout-account/${params.id}`,
        method: "GET",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    getListPayoutAccount: builder.query<any, { merchantId: string }>({
      query: ({ merchantId }) => ({
        url: `/merchant-management/${merchantId}/payout-account`,
        method: "GET",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    updatePayoutAccount: builder.mutation<DataResponse<any>, any>({
      query: (body: any) => ({
        url: `/merchant-management/${body.merchantId}/payout-account/${body.id}`,
        body,
        method: "PUT",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    validateMerchantByPublicKey: builder.mutation<any, { merchant_id: any,public_key: string }>({
      query: (body: any) => ({
        url: "/auth/verify-merchant-by-public_key",
        body,
        method: "POST",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    savePaymentDesc: builder.mutation<any, any>({
      query: (body: any) => ({
        url: `/merchant-management/${body?.merchant_id}/payment-description`,
        body,
        method: "PUT",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    getOperationType: builder.query<any, void>({
      query: () => ({
        url: `/merchant-management/operation-type`,
        method: "GET",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    getBookingLabel: builder.query<any, { merchantId: string }>({
      query: ({ merchantId }) => ({
        url: `/merchant-management/${merchantId}/booking-label`,
        method: "GET",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    getDetailRefundRequestMerchants: builder.query<any, { id: string }>({
      query: (params) => ({
        url: `/merchant-management/${params?.id}/refund-limit`,
        method: "GET",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    updateDetailRefundRequestMerchants: builder.mutation<any, any>({
      query: (body: any) => ({
        url: `/merchant-management/${body?.merchant_id}/refund-limit`,
        body,
        method: "PUT",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    verifyPaymentMethods: builder.query<
      DataResponse<any>,
      { merchantId: number | string}
    >({
      query: ({ merchantId }) => ({
        url: `/merchant-management/${merchantId}/verify-payment-methods`,
        method: "GET",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
  }),
});

export const {
  useGetMerchantDetailQuery,
  useLazyGetMerchantDetailQuery,
  useGetMerchantsQuery,
  useDownloadMerchantsMutation,
  useLazyGetMerchantsSelectQuery,
  useUpdateMerchantMutation,
  useCreateMerchantMutation,
  useGetMerchantsSelectQuery,
  useLazyGetMerchantsQuery,
  useDeleteMerchantMutation,
  useUpdateStatusMerchantMutation,
  useUpdateBillingInfoMutation,
  useLazyGetPayoutAccountDetailQuery,
  useGetListPayoutAccountQuery,
  useUpdatePayoutAccountMutation,
  useValidateMerchantByPublicKeyMutation,
  useCreateBillingInfoMutation,
  useGetMerchantBillingQuery,
  useLazyGetMerchantBillingQuery,
  useSavePaymentDescMutation,
  useGetOperationTypeQuery,
  useGetBookingLabelQuery,
  useGetDetailRefundRequestMerchantsQuery,
  useUpdateDetailRefundRequestMerchantsMutation,
  useLazyVerifyPaymentMethodsQuery,
} = merchantService;
